import { getContext } from '@fiverr-private/fiverr_context';
import { init, renderEntries, FunnelType } from '@fiverr-private/user_session';
import { renderBusinessRegistration } from '@fiverr-private/business_blocks';
import { sessionStorage } from '@fiverr-private/futile';
import pathfinder from '@fiverr-private/pathfinder';
import stats from '../../../lib/stats';
import { isBusinessExperience } from '../../../lib/business';
import { GLOBAL_MODAL_CONTAINERS } from '../constants';
import { EVENT_TYPES, trackEvent } from './events';
import {
    AUTO_SHOW_TRIGGER,
    FALLBACK_ROUTES,
    HANDLER_TYPES,
    QUERY_PARAMS,
    SIGN_UP_AUTO_SHOWN_KEY,
    TRIGGER_SELECTORS
} from './constants';

// **THIS MODULE IS TEMPORARY AND SHOULD BE REMOVED ONCE HEADER USES THE SESSION PACKAGE DIRECTLY**

/**
 * Gets requested parameter from current window SearchParams.
 * @param {String} paramName - The param name to get.
 * @return {String}
 */
const getQueryParams = (paramName) => new URLSearchParams(window.location.search)
    .get(paramName);

/**
 * Creates `UserSession` sign up modal options.
 * @return {Object}
 */
const getSignUpOptions = (optionsOverrides = {}) => {
    const referralEmail = getQueryParams(QUERY_PARAMS.REFERRAL_EMAIL);

    const referralOptions = referralEmail ? {
        isSticky: true,
        initialValues: {
            email: decodeURIComponent(referralEmail)
        }
    } : {};

    return {
        ...referralOptions,
        ...optionsOverrides
    };
};

const triggerSignInModal = () => {
    triggerModal({
        trigger: window.openSignInModal,
        handlerType: HANDLER_TYPES.SIGN_IN
    });
};

const triggerSignUpModal = (optionsOverrides = {}) => {
    triggerModal({
        trigger: window.openSignUpModal,
        options: getSignUpOptions(optionsOverrides),
        handlerType: HANDLER_TYPES.SIGN_UP
    });
};

/**
 * Triggers the signing modal. The user would be redirected to the fallback route
 * in case the trigger api method is not available or on mobile devices.
 *
 * @param {Function} trigger - The modal trigger method.
 * @param {Object} options - The options to be passed to the trigger method.
 * @param {String} handlerType - The signing flow handler type.
 */
const triggerModal = ({ trigger, options = {}, handlerType }) => {
    const { isTouch } = getContext();

    if (isTouch && !options.forceModal) {
        fallbackRedirect(handlerType);
        return;
    }

    if (!trigger) {
        stats.count(`signing_modal_trigger_failed.${handlerType.toLowerCase()}`);
        fallbackRedirect(handlerType);
        return;
    }

    trigger(options);
};

const fallbackRedirect = (handlerType) => {
    const fallbackRoute = pathfinder(FALLBACK_ROUTES[handlerType]);

    window.location.assign(fallbackRoute);
};

/**
 * @typedef UserSessionListener
 * @property {Function} action - The action to be triggered.
 * @property {String} eventType - The event type related to be tracked.
 */

/**
 * A map of element selectors, and their respective handler exported from `user_session` package.
 * @type {Object<UserSessionListener>}
 * @see https://github.com/fiverr/user_session_package#external-modal-triggers
 */
const LISTENER_MAP = {
    [HANDLER_TYPES.SIGN_IN]: {
        action: triggerSignInModal,
        eventType: EVENT_TYPES.SIGN_IN
    },
    [HANDLER_TYPES.SIGN_UP]: {
        action: triggerSignUpModal,
        eventType: EVENT_TYPES.SIGN_UP
    }
};

/**
 * Triggers user session click handler.
 * @param event - The html event triggered.
 * @param handlerType - The handler type (one of HANDLER_TYPES ).
 */
const userSessionClickHandler = (event, handlerType) => {
    const { target } = event;
    const {
        sourceComponent,
        eventType: customBigQueryEvent
    } = target.dataset;

    const { action, eventType } = LISTENER_MAP[handlerType];

    event.preventDefault();

    trackEvent({ eventType, customBigQueryEvent, sourceComponent });

    action();
};

/**
 * Binds listeners to all user session trigger elements on the page.
 */
const bindListeners = () => {
    bindListener(HANDLER_TYPES.SIGN_IN);
    bindListener(HANDLER_TYPES.SIGN_UP);
};

/**
 * Binds click handlers for all trigger elements selected by handler type.
 *
 * @param {String} handlerType - The signing flow handler type.
 */
const bindListener = (handlerType) => {
    const triggerSelector = TRIGGER_SELECTORS[handlerType];
    const triggerElements = document.querySelectorAll(triggerSelector);

    triggerElements.forEach((triggerElement) =>
        triggerElement.addEventListener('click', (event) => {
            userSessionClickHandler(event, handlerType);
        }));
};

const handleQueryParamTriggers = () => {
    if (getQueryParams(QUERY_PARAMS.SHOW_JOIN)) {
        sessionStorage.set(SIGN_UP_AUTO_SHOWN_KEY, true);

        triggerSignUpModal({
            triggerType: AUTO_SHOW_TRIGGER,
            forceModal: true
        });
    }
};

export const onLoad = () => {
    const inBusiness = isBusinessExperience(window.location.href);

    renderEntries(inBusiness ? FunnelType.business.name : undefined);

    if (inBusiness) {
        const container = document.getElementById(GLOBAL_MODAL_CONTAINERS.SIGN_UP);

        renderBusinessRegistration({ container });
    }

    bindListeners(); // TODO: Migrate this logic to user session init
    handleQueryParamTriggers();
    init();
};
